import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Instagram } from "lucide-react";
import { SITE } from "@/lib/site";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.76, 0, 0.24, 1];

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.95, ease: EASE, delay: 0.35 + i * 0.12 },
  }),
};

const KineticHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const yCoin = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const scrollToBook = () => scrollToSection("#book");

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="grain relative flex min-h-screen flex-col justify-end overflow-hidden bg-primary px-6 pb-16 pt-32 md:px-12 md:pb-24"
    >
      {/* Parallax decorative shapes */}
      <motion.div
        style={{ y: yBlob }}
        className="pointer-events-none absolute -right-24 top-24 h-[420px] w-[420px] rounded-full border border-secondary/20"
      />
      <motion.div
        style={{ y: yCoin, rotate: useTransform(scrollYProgress, [0, 1], [0, 45]) }}
        className="pointer-events-none absolute right-[8%] top-[18%] hidden h-40 w-40 rounded-full bg-secondary/90 md:block"
      >
        <div className="flex h-full w-full items-center justify-center">
          <span className="font-display text-6xl font-semibold text-primary">₹</span>
        </div>
      </motion.div>
      <div className="pointer-events-none absolute left-1/2 top-0 h-full w-px bg-primary-foreground/5" />

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-[1400px]">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-8 text-xs font-bold uppercase tracking-[0.28em] text-secondary md:text-sm"
        >
          Financial Well-being · Wealth Mindset · Judgment-free
        </motion.p>

        <h1 className="font-display text-[13vw] font-medium leading-[0.95] tracking-tightest text-primary-foreground md:text-[8.5vw] lg:text-[7.2vw]">
          {["Your Money Deserves", "a Plan, Not Just", "a Paycheck."].map((t, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block"
              >
                {i === 2 ? (
                  <span>
                    a <span className="italic text-secondary">Paycheck.</span>
                  </span>
                ) : (
                  t
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-12 flex flex-col gap-8 border-t border-primary-foreground/15 pt-8 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-xl text-lg font-light leading-relaxed text-primary-foreground/75 md:text-xl">
            I help individuals, groups, and institutions build financial well-being
            through simple, practical, unbiased conversations about money.
          </p>
          <div className="flex items-center gap-4">
            <button
              data-testid="hero-book-btn"
              onClick={scrollToBook}
              className="group flex items-center gap-3 rounded-full bg-secondary px-8 py-4 text-base font-semibold text-secondary-foreground transition-transform duration-300 hover:scale-[1.03]"
            >
              Book Your Session
              <ArrowDownRight
                size={20}
                className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1"
              />
            </button>
            <a
              href={SITE.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-instagram-btn"
              className="flex h-14 w-14 items-center justify-center rounded-full border border-primary-foreground/25 text-primary-foreground transition-colors duration-300 hover:border-secondary hover:text-secondary"
              aria-label="Instagram"
            >
              <Instagram size={22} />
            </a>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default KineticHero;
